/**
 * Der Ausgangskorb: was ohne Netz geschrieben wird, geht nicht verloren.
 *
 * Ein Reisebericht entsteht unterwegs, und unterwegs ist das Netz weg. Bisher
 * endete das so: Beitrag tippen, senden, Fehlermeldung — und der Text stand
 * nur so lange im Feld, wie die Seite offen war. Ein Neuladen, ein Absturz,
 * ein zu langer Ausflug in eine andere App: weg.
 *
 * Jetzt liegt er in **IndexedDB** und übersteht all das. Gesendet wird, sobald
 * die App wieder offen und online ist.
 *
 * **Was hier nicht geht, und warum:**
 *
 * - **Kein Versand im Hintergrund.** Die Background-Sync-Schnittstelle gibt es
 *   in Safari nicht. Auf iOS ist „sobald die App wieder offen ist" das
 *   Äußerste, was eine Web-App kann — versprochen wird deshalb auch nicht mehr.
 * - **Keine Videos.** Zweihundert Megabyte in IndexedDB sind eine andere
 *   Größenordnung als ein verkleinertes Foto, und der Upload in Stücken kann
 *   ohnehin fortsetzen, wenn die Leitung wiederkommt.
 *
 * Aufbewahrt werden **die Bilder selbst**, nicht ihre ids vom Server. Ein halb
 * hochgeladenes Foto hinterlässt eine id, die der Server nach einem Tag
 * wegräumt (`sweepOrphans`); ein Eintrag, der darauf zeigt, wäre nach einer
 * Nacht kaputt. Beim Senden wird deshalb frisch hochgeladen, und die Reste
 * räumt der Server weg.
 */

import { ApiError, api, inMaintenance, netzStand } from '../api/client'
import { maintenanceNow } from './maintenance'
import { nachrichtRumpf } from '../api/nachrichtRumpf'
import { AUSGANG, aendereIn, lauf as idbLauf, verbindungWegwerfen } from './idb'
import { aufnahmeAus, aufnahmeKopf } from './images'
import { vorschauHochladen } from './vorschau'
import { versatzDesGeraets } from './ortszeit'
import type { Media, Message, Ortsquelle, Zeitquelle , TagestrennerAngaben} from '../api/types'
import { WORTLAUT } from '../texte'

export interface OutboxEntry {
  /** Vergeben von IndexedDB; zugleich die Reihenfolge des Versands. */
  key: number
  /**
   * Was hier wartet — eine Nachricht, ein Highlight oder eine Wetterkarte
   * (#98, 1.54.0).
   *
   * **Fehlt das Feld, ist es eine Nachricht.** Was beim Umstieg schon im Korb
   * lag, hat es nicht, und dafür die Datenbankversion hochzuzählen hieße, eine
   * Migration für ein Feld zu schreiben, dessen Fehlen dieselbe Auskunft gibt.
   */
  art?: 'nachricht' | 'highlight' | 'wetter' | 'abstimmung'
  /**
   * A poll that waits for the net (1.80.0, from use: "Abstimmungen auch
   * offline"). Question and answers are all there is to it; the server
   * makes the post, and the key keeps a resend from becoming a second poll
   * (`Rundruf::vorhanden()`, 1.75.0).
   */
  abstimmung?: {
    question: string
    options: string[]
    multi: boolean
    draft: boolean
    /** The device's offset when it was made (1.87.0), like a message's. */
    tz_offset_min?: number
  }
  /**
   * A voice message that waits with its post (1.80.0, from use:
   * "Sprachnachrichten auch offline möglich machen"). The recording as it
   * came out of the conversion, and its length -- the server takes that
   * from a header, as on the direct road (`useAttachments.nimmTon`).
   */
  ton?: {
    daten: Blob
    dauerMs: number
  }
  /** The voice message's id once it is up -- the photos have `hochgeladen`. */
  tonHochgeladen?: number
  /**
   * A day separator written without a net (#113). Since 1.115.0 (#130) it is
   * a post of its own: an entry of nothing but the line goes out through
   * `POST …/tagestrenner` (`schicken()`). An entry from before the update
   * may still carry text or photos too; the line goes first as its own
   * post, then the rest, so that nothing waits forever.
   */
  tagestrenner?: TagestrennerAngaben
  /**
   * The weather card that waits for the net (#98): the device's place and
   * the moment of the tap. Nothing else -- the weather itself is the
   * server's to fetch, for exactly this moment, once there is a net
   * (`WeatherController`, `at` with `located`). From use: "man sollte
   * offline dennoch Wetter hinzufügen können; die Wetterkarte merkt sich
   * dann nur Ort und Zeit."
   */
  wetter?: {
    lat: number
    lon: number
    at: string
    /** The zone of the device at the tap (1.86.0). */
    tz_offset_min: number
    draft: boolean
  }
  /**
   * Die Uhr, auf der der Absender beim **Schreiben** stand — Minuten zu UTC.
   *
   * **Gemerkt beim Ablegen und nicht beim Senden** (#71). Der Korb holt
   * Stunden später nach, und womöglich aus einer anderen Zone: wer abends über
   * eine Grenze fährt und erst dort Netz hat, bekäme sonst die Uhr des
   * Ankommens an den Text geschrieben. Dieselbe Überlegung wie beim Highlight
   * („Der Ausgangskorb nimmt die Uhr des Punktes mit").
   *
   * Fehlt das Feld, lag der Eintrag schon vor dieser Version im Korb — dann
   * entscheidet `nachrichtRumpf()` beim Senden, und das ist die beste noch
   * verfügbare Auskunft.
   */
  tzOffsetMin?: number
  tripId: number
  /** Gesetzt: eine private Nachricht an genau diesen Menschen. */
  withUser?: number
  /**
   * Into the draft chat (#85).
   *
   * Kept in the basket like the quote: a draft written without net must
   * land in the draft later, not in the chat -- that is the whole point of
   * a draft. **Where in it, the basket does not know** (#130): without a net
   * there is no inserting, only the end.
   */
  entwurf?: boolean
  body: string
  /**
   * Der Schlüssel, an dem der Server dieselbe Nachricht wiedererkennt.
   *
   * Er wird beim Ablegen vergeben und bleibt über alle Versuche derselbe --
   * genau darin liegt sein Zweck. Ein Versuch kann durchgehen und trotzdem
   * scheitern: die Nachricht ist auf dem Server, die Antwort geht auf dem
   * Rückweg verloren, oder iOS beendet die App zwischen „abgeschickt" und
   * „abgehakt". Beim nächsten Anlauf entsteht dann keine zweite Nachricht.
   */
  clientKey: string
  /**
   * Worauf sich die Nachricht bezieht.
   *
   * Muss mit in den Korb: eine Antwort, die ohne Netz geschrieben wurde und
   * später ohne ihren Anfang herauskommt, hat den halben Sinn verloren -- und
   * zwar genau den, für den es den Bezug gibt.
   */
  replyToId?: number
  replyToMediaId?: number
  /** Die fertig verkleinerten Fotos, wie sie hochgeladen werden sollen. */
  bilder: Blob[]
  /**
   * The thumbnails to the photos, one per entry of `bilder` (1.3.0), `null`
   * where none was computed. Optional, so entries written before this field
   * still read -- they simply have none.
   */
  vorschauen?: (Blob | null)[]
  /**
   * Was schon oben ist -- je Bild, in derselben Reihenfolge (Codereview A11).
   *
   * **Ein Versuch kann in der Mitte scheitern.** Drei Fotos, das dritte reißt
   * ab: bis 1.34.0 begann der nächste Versuch wieder beim ersten, lud es noch
   * einmal hoch und liess das erste Exemplar als herrenlose Datei liegen --
   * auf einer Mobilfunkverbindung ist das der teuerste Teil der ganzen
   * Übung, und bei fünf Fotos und wackligem Netz kam der Beitrag womöglich
   * nie durch.
   *
   * Gemerkt wird die Nummer, nicht das Bild: was oben ist, liegt beim Server.
   * Der räumt ein Foto, das an nichts hängt, erst nach einem Tag weg
   * (`MEDIA_ORPHAN_TTL`) -- länger, als ein Eintrag im Korb wartet.
   */
  hochgeladen?: (number | null)[]
  /**
   * When `hochgeladen` was last written (1.78.0).
   *
   * The ids in it point at photos the server sweeps after a day when nothing
   * hangs on them (`MEDIA_ORPHAN_TTL`). The sentence above ("länger, als ein
   * Eintrag im Korb wartet") holds for most entries, not for all: an entry
   * that started, and then waited two days in a dead zone, sent ids that
   * were gone -- a 400, a refusal, and "Nochmal" failed the same way every
   * time, although the bytes were still here. Past `HOCHGELADEN_GILT_MS` the
   * list is not trusted and the photos go up again.
   */
  hochgeladenAm?: string
  /**
   * Was ein wartendes Highlight ausmacht.
   *
   * Ohne Netz entsteht es genau wie mit: Ort (meistens vom Gerät, siehe
   * `lib/ortung.ts`), Zeit, Text, Fotos. Nur der Weg hinaus wartet.
   */
  highlight?: {
    lat: number
    lon: number
    position_source: Ortsquelle
    title: string
    body: string
    happened_at: string
    time_source?: Zeitquelle
    /**
     * Die Uhr an diesem Punkt — Minuten zu UTC.
     *
     * **Der Zeitpunkt ginge auch ohne sie richtig hinaus**: `happened_at` steht
     * als UTC im Korb, und umgerechnet wurde schon im Formular. Verloren ginge
     * nur, in welcher Uhr er später zu lesen ist — und das ist genau die
     * Angabe, die jemand von Hand nachgestellt hat, weil die Reise-Uhr an
     * dieser Stelle nicht galt. Die Fahrt über eine Zonengrenze ist die
     * Gelegenheit, bei der man am ehesten ohne Empfang ist.
     */
    tz_offset_min: number
    show_in_chat: boolean
    /**
     * Fotos, die es schon zum Server geschafft haben.
     *
     * Der gemischte Fall: zwei Bilder gingen hoch, beim dritten war das Netz
     * weg. Ohne diese Nummern lägen die ersten beiden für immer ohne Highlight
     * herum und würden irgendwann als Reste weggeräumt.
     */
    media_ids?: number[]
    /**
     * Spots & Aktivitäten, die vor dem Sichern gewählt wurden.
     *
     * Sie reisen im Rumpf mit und nicht in einer zweiten Anfrage: der Korb
     * schickt genau diesen Rumpf später noch einmal, und eine zweite Anfrage
     * wäre hier verloren.
     */
    pois?: Array<{
      name: string
      lat: number
      lon: number
      category: string
      /** Was dort war — geht denselben Weg mit, sonst wäre er ohne Netz weg. */
      note?: string
    }>
  }
  /** Wann geschrieben -- die Sprechblase zeigt diese Zeit, nicht die des Versands. */
  angelegt: string
  versuche: number
  /** Die letzte Absage des Servers, falls es eine gab. */
  problem?: string
}

/**
 * Was zum Ablegen nötig ist.
 *
 * `body` gehört zur Nachricht und fehlt bei einem Highlight -- deshalb hier
 * freiwillig und beim Ablegen mit einer leeren Zeichenkette unterlegt. Ein
 * Highlight hat seinen Text in `highlight.body`.
 */
type Neu = Omit<OutboxEntry, 'key' | 'angelegt' | 'versuche' | 'clientKey' | 'body'> & {
  clientKey?: string
  body?: string
}

/**
 * Eine Arbeit auf dem Ausgangsspeicher.
 *
 * **Geöffnet wird die Datenbank in `idb.ts`, nicht hier.** Sie hat eine
 * Version für alle Speicher zusammen; wer sie an zwei Stellen mit zwei
 * Nummern öffnet, legt die andere Stelle lahm. Der Grund steht dort
 * ausführlich.
 */
function lauf<T>(modus: IDBTransactionMode, arbeit: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return idbLauf(AUSGANG, modus, arbeit)
}

/**
 * The stored form of an entry: photos as bytes (`Bilddaten`), not as blobs.
 * Only `merken()` builds one; everything after that changes fields of it.
 */
type Gespeichert = Omit<OutboxEntry, 'bilder' | 'vorschauen' | 'ton'> & {
  bilder: (Blob | Bilddaten)[]
  vorschauen?: (Blob | Bilddaten | null)[]
  ton?: { daten: Blob | Bilddaten; dauerMs: number }
}

/**
 * **Change fields of the stored entry, never write the one in hand back**
 * (1.78.0).
 *
 * Here stood `store.put({ ...eintrag, … })` twice, with the entry as
 * `alle()` hands it out. That did three things nobody meant:
 *
 * - it wrote the photos back **as blobs** -- `alle()` turns the bytes into
 *   blobs for the caller, and the spread took them along. After the first
 *   attempt the entry was stored in exactly the form 1.43.0 had removed
 *   ("Ein wartendes Foto lässt sich nicht mehr lesen").
 * - `vermerken()` wrote the state from the start of the round, so the
 *   progress `schicken()` had just saved (`hochgeladen`) was gone, and the
 *   next attempt uploaded the same photos again.
 * - an entry discarded while it was being sent came back with the next put,
 *   and went out after all.
 *
 * So the change is a function of the stored record, read and written in one
 * transaction, and a record that is gone stays gone.
 */
function aendern(key: number, wie: (alt: Gespeichert) => Gespeichert): Promise<boolean> {
  return aendereIn<Gespeichert>(AUSGANG, key, wie)
}

/**
 * How long uploaded ids in `hochgeladen` are trusted: half the server's
 * default day, so that a server set tighter still has room (reason at
 * `OutboxEntry.hochgeladenAm`).
 */
const HOCHGELADEN_GILT_MS = 12 * 3600 * 1000

/**
 * Keys that one attempt is working on right now -- the automatic round and
 * the manual "Nochmal" must not send the same entry side by side (1.78.0):
 * the key kept a second post away, but the photos went up twice.
 */
const inArbeit = new Set<number>()

/**
 * The entry was discarded while it was being sent (1.78.0) -- not a failure,
 * so nothing is noted and nothing is said: the person asked for exactly this.
 */
class Verworfen extends Error {}

/** Is the entry still in the basket? Asked right before the post goes out. */
async function nochDa(key: number): Promise<boolean> {
  return (await lauf<IDBValidKey | undefined>('readonly', (store) => store.getKey(key))) !== undefined
}

// --- Melden, wenn sich etwas ändert -----------------------------------------

const hoerer = new Set<() => void>()

export function abonnieren(bei: () => void): () => void {
  hoerer.add(bei)
  return () => hoerer.delete(bei)
}

function melden(): void {
  for (const h of hoerer) h()
}

// --- Ablegen, lesen, wegwerfen ----------------------------------------------

/**
 * Wie ein Foto **im Korb** liegt: als rohe Bytes, nicht als Blob.
 *
 * **Und das ist der Kern der Sache** (1.43.0). Ein Blob in der IndexedDB ist
 * auf iOS nur ein Verweis auf Daten, die WebKit anderswo haelt -- und die
 * nimmt es irgendwann weg. Aus dem Betrieb, mit Bildschirmfotos ueber vier
 * Versionen belegt: dasselbe wartende Foto ist beim Anlegen lesbar, an der
 * Blase zu sehen, und fuenf Minuten spaeter beim Senden fort.
 *
 * Zwei Anlaeufe davor haben das nicht geheilt, und beide waren gut begruendet:
 * die Bytes beim Lesen herausholen (1.37.0/1.38.0) hilft nur, solange sie noch
 * da sind, und eine frisch geoeffnete Datenbank (1.41.0) holt keine Daten
 * zurueck, die es nicht mehr gibt. **Was bleibt, ist, sie gar nicht erst aus
 * der Hand zu geben:** ein `ArrayBuffer` wird in den Datensatz selbst
 * geschrieben und gehoert danach der Datenbank, nicht einem Dateiverweis.
 *
 * Der Preis steht hier, damit ihn niemand suchen muss: `alle()` liest den
 * ganzen Korb, und die Bytes kommen dabei jedes Mal mit. Das faellt nicht ins
 * Gewicht, weil `melden()` nur laeuft, wenn sich am Korb wirklich etwas
 * aendert -- beim Ablegen, beim Senden, beim Verwerfen. Ein Zeitgeber ruft es
 * nicht.
 */
interface Bilddaten {
  daten: ArrayBuffer
  typ: string
}

/** Beim Ablegen: aus einem frischen Blob werden Bytes. */
async function alsBytes(blob: Blob): Promise<Bilddaten> {
  return { daten: await blob.arrayBuffer(), typ: blob.type || 'image/jpeg' }
}

/** Beim Lesen: aus Bytes wird ein Blob, der im Speicher steht. */
function alsBlob(was: Blob | Bilddaten | null | undefined): Blob | null {
  if (was === null || was === undefined) return null
  // Ein Eintrag von vor 1.43.0 traegt noch einen echten Blob.
  if (was instanceof Blob) return was
  return new Blob([was.daten], { type: was.typ })
}

export async function merken(eintrag: Neu): Promise<void> {
  /*
   * **Gelesen wird hier und nicht spaeter.** In diesem Augenblick ist der Blob
   * gerade erst entstanden (`prepareImage`), also sicher lesbar; jede Minute
   * spaeter ist eine Minute, in der ihn iOS wegnehmen kann.
   */
  const bilder = await Promise.all((eintrag.bilder ?? []).map(alsBytes))
  const vorschauen = await Promise.all(
    (eintrag.vorschauen ?? []).map(async (v) => (v === null ? null : await alsBytes(v))),
  )
  // The recording the same way and for the same reason (1.80.0).
  const ton = eintrag.ton === undefined
    ? undefined
    : { daten: await alsBytes(eintrag.ton.daten), dauerMs: eintrag.ton.dauerMs }

  await lauf('readwrite', (store) =>
    store.add({
      ...eintrag,
      bilder,
      vorschauen,
      ...(ton === undefined ? {} : { ton }),
      body: eintrag.body ?? '',
      clientKey: eintrag.clientKey ?? neuerSchluessel(),
      // **Hier wird die Uhr gestempelt, und nur hier** (#71): das Ablegen ist
      // der Augenblick des Schreibens. Was der Korb später sendet, kann in
      // einer anderen Zone geschehen.
      tzOffsetMin: eintrag.tzOffsetMin ?? versatzDesGeraets(),
      angelegt: new Date().toISOString(),
      versuche: 0,
    }),
  )
  melden()
}

/**
 * Ein Schlüssel, der zweimal auf der Welt nicht vorkommt.
 *
 * `randomUUID` gibt es nur in einem sicheren Kontext (HTTPS oder localhost);
 * über eine ungesicherte Verbindung im Heimnetz gäbe es sonst gar keinen
 * Schlüssel -- und damit auch keinen Schutz vor der doppelten Nachricht.
 */
export function neuerSchluessel(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()

  const zufall = new Uint8Array(16)
  crypto.getRandomValues(zufall)
  return [...zufall].map((z) => z.toString(16).padStart(2, '0')).join('')
}

/** Alle wartenden Einträge, älteste zuerst. */
export async function alle(): Promise<OutboxEntry[]> {
  try {
    const eintraege = await lauf<OutboxEntry[]>('readonly', (store) => store.getAll())
    return eintraege
      .map((e) => ({
        ...e,
        // Nach aussen bleibt es ein Blob -- nur einer, der im Speicher steht
        // und keinem Dateiverweis mehr folgt (Grund an `Bilddaten`).
        bilder: (e.bilder ?? []).map(alsBlob).filter((b): b is Blob => b !== null),
        vorschauen: (e.vorschauen ?? []).map(alsBlob),
        ton: tonAlsBlob((e as unknown as Gespeichert).ton),
      }))
      .sort((a, b) => a.key - b.key)
  } catch {
    // Ein Browser ohne IndexedDB (oder mit gesperrtem Speicher) soll die
    // Anwendung nicht anhalten -- dann gibt es eben keinen Korb.
    return []
  }
}

/** The stored recording back as a blob -- `undefined` stays `undefined`. */
function tonAlsBlob(ton: Gespeichert['ton']): OutboxEntry['ton'] {
  if (ton === undefined) return undefined
  const daten = alsBlob(ton.daten)
  return daten === null ? undefined : { daten, dauerMs: ton.dauerMs }
}

export async function verwerfen(key: number): Promise<void> {
  await lauf('readwrite', (store) => store.delete(key) as unknown as IDBRequest<undefined>)
  melden()
}

// --- Senden ------------------------------------------------------------------

let laeuft = false

/**
 * Schickt raus, was wartet -- der Reihe nach.
 *
 * **Einer nach dem anderen, und beim ersten Netzfehler ist Schluss.** Parallel
 * wäre schneller und falsch: die Reihenfolge der Beiträge ist bei einem
 * Reisebericht eine Aussage, und zwanzig gleichzeitige Fehlversuche auf einer
 * wackligen Leitung sind keine Hartnäckigkeit, sondern Lärm.
 *
 * Eine **Absage des Servers** (abgeschlossene Reise, pausierte Empfängerin)
 * bleibt am Eintrag stehen, statt ihn wegzuwerfen: der Text gehört dem
 * Menschen, der ihn geschrieben hat. Er sieht den Grund an der Sprechblase und
 * entscheidet selbst. Ein **Serverfehler** ist keine Absage und wird später
 * wieder versucht (Codereview E5) -- ein 503 beim Neustart darf keinen
 * Reisebericht kosten.
 *
 * @return wie viele tatsächlich rausgegangen sind
 */
export async function senden(): Promise<number> {
  /*
   * **`netzStand()` und nicht `navigator.onLine`** (Codereview E5).
   *
   * `navigator.onLine` sagt nur, ob das Gerät irgendwo hängt -- ein WLAN ohne
   * Weg nach draußen meldet dort „online". `netzStand()` weiß es aus den
   * Anfragen selbst, und genau dieselbe Quelle zeichnet den Netzstreifen.
   * Zwei Quellen für dieselbe Frage liefen hier auseinander: der Streifen
   * sagte „kein Netz", und der Korb versuchte es trotzdem.
   */
  /*
   * **In a maintenance nothing goes out** (#131): every entry would get the
   * same 503, and each would count an attempt. The page reloads when the
   * server lets this device through again, and the round after the start
   * sends what waited.
   */
  if (laeuft || !netzStand() || maintenanceNow() !== null) return 0
  laeuft = true

  let raus = 0
  /*
   * **Wie viele Einträge hintereinander am Netz gescheitert sind** (1.40.0).
   *
   * Hier stand „ein Netzfehler, und Schluss für diese Runde". Das ist richtig,
   * solange der Netzfehler auch einer ist -- und genau das ist er nicht immer:
   * ein abgebrochener Upload kommt ebenfalls als `ApiError(0)` zurück
   * (`api/client.ts`, `xhr.onerror`). Ein einzelner Eintrag, der aus welchem
   * Grund auch immer nicht hinausgeht, hielt damit **alles hinter sich** auf,
   * und zwar für immer.
   *
   * Aus dem Betrieb gemeldet als etwas ganz anderes: „ich lege ohne Netz einen
   * Kartenpunkt in den Ausgangskorb, und wenn ich wieder online bin, finde ich
   * ihn nicht mehr." Er lag hinter einem Beitrag mit Fotos, der hängen
   * geblieben war.
   *
   * **„Keine Verbindung" heißt zweierlei, und am Fehler sind die beiden nicht
   * zu unterscheiden:** entweder ist wirklich kein Netz da, oder nur dieser
   * eine Eintrag geht nicht hinaus. Deshalb wird nach dem ersten Fehlschlag
   * der nächste Eintrag noch versucht. Scheitert der auch, ist tatsächlich
   * kein Netz da, und die Runde hört auf wie bisher. Kommt er durch, lag es am
   * ersten Eintrag allein, und der Rest wird abgearbeitet.
   *
   * Gezählt wird also, wie viele **direkt nacheinander** am Netz gescheitert
   * sind; bei zweien wird abgebrochen. Kostet einen zusätzlichen Anlauf, wenn
   * wirklich kein Netz da ist.
   */
  let netzfehlerInFolge = 0
  /*
   * **Was waehrend des Laufs dazukommt, geht in derselben Runde mit**
   * (1.44.0).
   *
   * Die Liste wurde einmal gelesen und dann abgearbeitet. Seit 1.44.0 stellt
   * sich hinten an, wer schreibt, waehrend noch etwas wartet
   * (`Composer.submit()`) -- und genau dieser Beitrag kam in der laufenden
   * Runde nicht mehr vor. Er blieb als wartende Blase stehen, bis `online`
   * oder ein Wechsel in die App die naechste Runde ausloeste, und das kann
   * lange dauern.
   *
   * Geholt wird deshalb **je Eintrag der naechste** statt einmal die ganze
   * Liste. Der Schluessel von IndexedDB waechst, `zuletzt` merkt sich den
   * hoechsten, der drankam -- was dazwischen dazukommt, liegt darueber und
   * geht mit. Der Preis ist ein `getAll` je Eintrag auf einem Speicher mit
   * einer Handvoll Zeilen.
   */
  let zuletzt = -1
  try {
    for (;;) {
      // Der naechste, den diese Runde noch nicht hatte. Ein Eintrag, der
      // gerade am Netz gescheitert ist, behaelt seinen Schluessel; der liegt
      // nicht ueber `zuletzt`, also kommt er hier nicht noch einmal dran.
      const eintrag = (await alle()).find((warte) => warte.key > zuletzt)
      if (eintrag === undefined) break
      zuletzt = eintrag.key

      // Ein Eintrag, den der Server schon einmal abgelehnt hat, wird nicht
      // von selbst wieder versucht -- sonst liefe er in dieselbe Wand.
      if (eintrag.problem !== undefined) continue
      // "Nochmal" has it in hand right now.
      if (inArbeit.has(eintrag.key)) continue

      inArbeit.add(eintrag.key)
      try {
        await schicken(eintrag)
        await verwerfen(eintrag.key)
        raus++
        netzfehlerInFolge = 0
      } catch (fehler) {
        if (fehler instanceof Verworfen) continue
        // The maintenance began in the middle of the round (#131): no
        // attempt is counted, nothing stands at the entry, and the rest
        // would get the same answer.
        if (inMaintenance(fehler)) break
        /*
         * **Ein Serverfehler ist keine Absage** (Codereview E5).
         *
         * Hier stand „alles, was kein Netzfehler ist, ist ein Problem", und
         * damit blieb ein Eintrag nach einem einzigen 500er oder 503er für
         * immer liegen -- er wird ja nicht von selbst wieder versucht. Ein
         * Server, der gerade neu startet oder überlastet ist, hätte so einen
         * Reisebericht gekostet, und niemand hätte es gemerkt außer an einer
         * Sprechblase, die nicht wegging.
         *
         * Eine **Absage** ist etwas anderes: die Reise ist abgeschlossen, die
         * Empfängerin pausiert. Die steht am Eintrag, und der Mensch
         * entscheidet. Das sind die 4xx.
         */
        const netz = fehler instanceof ApiError && fehler.offline
        const spaeter = netz || (fehler instanceof ApiError && fehler.status >= 500)
        await vermerken(eintrag, spaeter ? undefined : beschreiben(fehler))
        // Ein Serverfehler betrifft diesen einen Eintrag -- weiter mit dem
        // nächsten. Bei einem Netzfehler entscheidet der nächste Eintrag, ob
        // es an der Leitung lag (Grund oben an `netzfehlerInFolge`).
        if (!netz) {
          netzfehlerInFolge = 0
          continue
        }
        netzfehlerInFolge++
        if (netzfehlerInFolge >= 2) break
      } finally {
        inArbeit.delete(eintrag.key)
      }
    }
  } finally {
    laeuft = false
  }

  if (raus > 0) melden()
  return raus
}

/** Einen einzelnen Eintrag noch einmal versuchen -- auf Knopfdruck. */
export async function nochmal(key: number): Promise<void> {
  const eintrag = (await alle()).find((e) => e.key === key)
  if (eintrag === undefined) return
  // The round is sending it this very moment; its result will show.
  if (inArbeit.has(key)) return

  inArbeit.add(key)
  try {
    await schicken(eintrag)
    await verwerfen(eintrag.key)
  } catch (fehler) {
    if (fehler instanceof Verworfen) return
    // A maintenance is waiting, not a refusal (#131) -- nothing to note.
    if (inMaintenance(fehler)) return
    const netz = fehler instanceof ApiError && fehler.offline
    await vermerken(eintrag, netz ? undefined : beschreiben(fehler))
  } finally {
    inArbeit.delete(key)
  }
  melden()
}

async function schicken(eintrag: OutboxEntry): Promise<unknown> {
  // Ids older than the server keeps an unattached photo are not trusted
  // (`hochgeladenAm`); the bytes are still here, so they go up again.
  const frisch = eintrag.hochgeladenAm !== undefined
    && Date.now() - Date.parse(eintrag.hochgeladenAm) < HOCHGELADEN_GILT_MS
  const schon = frisch ? [...(eintrag.hochgeladen ?? [])] : []
  const ids: number[] = []
  for (const i of eintrag.bilder.keys()) {
    // **Was beim letzten Versuch schon oben war, bleibt oben** (Codereview
    // A11). Sonst begänne jeder Versuch wieder beim ersten Foto.
    const fertig = schon[i] ?? null
    if (fertig !== null) {
      ids.push(fertig)
      continue
    }

    // **Erst lesen, dann senden** -- Grund und zweiter Anlauf in `bildLesen()`.
    const daten = await bildLesen(eintrag, i)

    // Ort und Zeit stehen noch im Bild -- nur gelesen hat sie beim Einlegen
    // niemand, weil damals nichts hinausging. Ohne diese Zeile käme ein
    // Beitrag aus dem Korb ohne die Angaben an, die derselbe Beitrag mit Netz
    // mitgebracht hätte.
    const media = await api.upload<Media>(
      `/api/trips/${eintrag.tripId}/media`,
      daten,
      undefined,
      aufnahmeKopf(await aufnahmeAus(daten)),
    )
    ids.push(media.id)
    // The thumbnail after the photo, as the upload hook does it (1.3.0).
    await vorschauHochladen(media.id, eintrag.vorschauen?.[i] ?? null)

    /*
     * **Sofort merken und nicht am Ende.** Der Sinn der Liste ist der
     * Abbruch mittendrin; wer sie erst nach der Schleife schreibt, hat sie
     * genau dann nicht, wenn sie gebraucht wird.
     */
    schon[i] = media.id
    const liste = [...schon]
    const am = new Date().toISOString()
    await aendern(eintrag.key, (alt) => ({ ...alt, hochgeladen: liste, hochgeladenAm: am }))
  }

  /*
   * **The voice message after the photos** (1.80.0), to its own address
   * with its length in a header -- the same call as on the direct road
   * (`useAttachments.nimmTon`). Its id is kept at once like a photo's, so a
   * break after it does not send it up twice.
   */
  if (eintrag.ton !== undefined) {
    const tonSchon = frisch ? eintrag.tonHochgeladen : undefined
    if (tonSchon !== undefined) {
      ids.push(tonSchon)
    } else {
      const medium = await api.upload<Media>(
        `/api/trips/${eintrag.tripId}/media/voice`,
        eintrag.ton.daten,
        undefined,
        { 'x-dauer-ms': String(Math.round(eintrag.ton.dauerMs)) },
      )
      ids.push(medium.id)
      const am = eintrag.hochgeladenAm !== undefined && frisch ? eintrag.hochgeladenAm : new Date().toISOString()
      await aendern(eintrag.key, (alt) => ({ ...alt, tonHochgeladen: medium.id, hochgeladenAm: am }))
    }
  }

  // **Discarded meanwhile, then it stays discarded** (1.78.0). Until here a
  // tap on "Verwerfen" during the upload was undone twice over: the entry
  // came back with the next write, and the post went out anyway.
  if (!(await nochDa(eintrag.key))) throw new Verworfen()

  // A weather card (#98): place and moment go to the server, which asks
  // Apple for that moment; `located` says it was the device's place, and
  // the key keeps a resend from becoming a second card.
  if (eintrag.art === 'wetter' && eintrag.wetter !== undefined) {
    return api.post(`/api/trips/${eintrag.tripId}/weather`, {
      lat: eintrag.wetter.lat,
      lon: eintrag.wetter.lon,
      at: eintrag.wetter.at,
      tz_offset_min: eintrag.wetter.tz_offset_min,
      located: true,
      draft: eintrag.wetter.draft,
      client_key: eintrag.clientKey,
    })
  }

  // A poll (1.80.0): question and answers, and the key from the form, so
  // that a resend after a lost answer is the same poll.
  if (eintrag.art === 'abstimmung' && eintrag.abstimmung !== undefined) {
    return api.post(`/api/trips/${eintrag.tripId}/polls`, {
      ...eintrag.abstimmung,
      client_key: eintrag.clientKey,
    })
  }

  // Ein Highlight geht denselben Weg wie eine Nachricht: erst die Fotos, dann
  // der Eintrag mit ihren Nummern. Der `client_key` ist dabei nicht Beiwerk --
  // ein Versuch kann durchgehen, während die Antwort verlorengeht, und ohne
  // ihn stünde der Punkt danach zweimal auf der Karte.
  if (eintrag.art === 'highlight' && eintrag.highlight !== undefined) {
    return api.post(`/api/trips/${eintrag.tripId}/highlights`, {
      ...eintrag.highlight,
      media_ids: [...(eintrag.highlight.media_ids ?? []), ...ids],
      client_key: eintrag.clientKey,
    })
  }

  // A separator goes as its own post, with a key of its own derived from the
  // entry's -- a resend finds it. An entry of nothing but the line ends
  // there; one from before 1.115.0 with text sends the text after it.
  if (eintrag.tagestrenner) {
    const trenner = await api.post<Message>(`/api/trips/${eintrag.tripId}/tagestrenner`, {
      day_separator: eintrag.tagestrenner,
      draft: eintrag.entwurf === true,
      client_key: `${eintrag.clientKey}-t`.slice(0, 64),
      tz_offset_min: eintrag.tzOffsetMin,
    })
    if (eintrag.body.trim() === '' && ids.length === 0) return trenner
  }

  return api.post<Message>(
    `/api/trips/${eintrag.tripId}/messages`,
    // **Derselbe Rumpf wie in der Eingabezeile** -- und der gemerkte Versatz
    // dazu: geschrieben wurde damals, gesendet wird jetzt.
    nachrichtRumpf(
      {
        body: eintrag.body,
        mediaIds: ids,
        clientKey: eintrag.clientKey,
        replyToId: eintrag.replyToId,
        replyToMediaId: eintrag.replyToMediaId,
        entwurf: eintrag.entwurf,
      },
      eintrag.tzOffsetMin,
    ),
    { with: eintrag.withUser },
  )
}

/**
 * Die Bytes eines wartenden Fotos -- **mit einem zweiten Anlauf**.
 *
 * Der erste Teil ist seit 1.37.0 da: das Bild kommt aus der IndexedDB, und was
 * von dort zurueckkommt, ist nicht zwingend noch lesbar. Ging es ungelesen in
 * `xhr.send()`, brach der Upload ohne Antwort ab -- und das heisst in
 * `api/client.ts` `ApiError(0)`, also "kein Netz". Der Korb versuchte es dann
 * ewig weiter, die Blase sagte "wartet auf Netz", und das Netz war die ganze
 * Zeit da.
 *
 * **Der zweite Anlauf kam mit 1.41.0 dazu, und er kam aus einem Beleg.** Aus
 * dem Betrieb, mit Bildschirmfoto: die Meldung "Ein wartendes Foto laesst sich
 * auf diesem Geraet nicht mehr lesen" stand an der Blase -- und **derselbe
 * Eintrag** ging nach einem Neustart der App anstandslos hinaus. Der
 * Unterschied zwischen beiden Laeufen ist die Verbindung zur Datenbank: WebKit
 * gibt die Datenquelle hinter einem Blob irgendwann frei, und was an einer
 * alten Verbindung haengt, ist dann leer.
 *
 * **Und seit 1.42.0 nimmt die Anzeige denselben Weg.** Aus dem Betrieb, wieder
 * mit Bildschirmfotos: an der wartenden Blase standen erst drei Fotos, dann
 * keins mehr, und nach einem Neustart wieder drei. Der Lesefehler ist
 * derselbe -- abgefangen war er aber nur hier im Sendeweg, und `Wartend` hatte
 * weiter genau einen Versuch. Deshalb ist diese Funktion ausgefuehrt und wird
 * von dort gerufen: zwei Stellen mit derselben Regel laufen auseinander.
 *
 * Also wird genau das nachgestellt, was ein Neustart tut: Verbindung weg,
 * Eintrag frisch lesen, noch einmal versuchen. Erst wenn auch das nichts
 * bringt, ist es eine Absage -- die steht dann an der Blase, mit einem Knopf
 * daneben.
 */
export async function bildLesen(eintrag: OutboxEntry, i: number): Promise<Blob> {
  const bytes = async (blob: Blob): Promise<Blob> => {
    /*
     * **Diese Tuer gibt es fuer den Prueflauf**, wie `kodierungen` beim Video,
     * und aus demselben Grund: das Chromium der Pruefumgebung haelt einen Blob
     * aus der IndexedDB lesbar, solange ihn jemand haelt. Der Lesefehler, um
     * den es hier geht, tritt dort **nie** ein -- und damit liefe der zweite
     * Anlauf nirgends. Ein Rueckfall, den nichts je durchlaeuft, ist keiner.
     *
     * Im Betrieb wird sie nicht gesetzt. Gezaehlt wird herunter, damit sich
     * genau der Fall nachstellen laesst, um den es geht: erster Anlauf
     * scheitert, zweiter geht durch.
     */
    const tuer = (globalThis as { __tbLeseFehler?: number }).__tbLeseFehler
    if (typeof tuer === 'number' && tuer > 0) {
      ;(globalThis as { __tbLeseFehler?: number }).__tbLeseFehler = tuer - 1
      throw new Error('Prüfstand: Lesefehler')
    }
    return new Blob([await blob.arrayBuffer()], { type: blob.type || 'image/jpeg' })
  }

  const erstes = eintrag.bilder[i]
  if (erstes === undefined) throw new Error(WORTLAUT.technik.fehler.bildNichtLesbar)
  try {
    return await bytes(erstes)
  } catch {
    // Der erste Anlauf ist der haeufige Fall; der zweite steht unten.
  }

  verbindungWegwerfen()
  const frisch = (await alle()).find((e) => e.key === eintrag.key)?.bilder[i]
  if (frisch === undefined) throw new Error(WORTLAUT.technik.fehler.bildNichtLesbar)
  try {
    return await bytes(frisch)
  } catch {
    throw new Error(WORTLAUT.technik.fehler.bildNichtLesbar)
  }
}

async function vermerken(eintrag: OutboxEntry, problem: string | undefined): Promise<void> {
  // Of the stored record, not of the one in hand -- reason at `aendern()`.
  await aendern(eintrag.key, (alt) => ({ ...alt, versuche: alt.versuche + 1, problem }))
  melden()
}

function beschreiben(fehler: unknown): string {
  return fehler instanceof Error ? fehler.message : WORTLAUT.allgemein.schiefgelaufen
}
